import { api } from "@/lib/api";

export type ProductKnowledgeBase = {
  publicoAlvoOverride?: string;
  tomVozOverride?: string[];
  personas?: BriefingPersona[];
  concorrentes?: string[];
  resultados?: BriefingResultado[];
  notas?: string;
};

export type BriefingProduct = {
  id: string;
  nome: string;
  tipo: string;
  preco: string;
  moeda?: string;
  formasPagamento?: string[];
  descricao?: string;
  entregaveis?: string;
  formatoEntrega?: string;
  garantia?: string;
  tempoAcesso?: string;
  mecanismoUnicoSolucao?: string;
  bonus?: string[];
  beneficios?: {
    funcionais?: string[];
    emocionais?: string[];
    dimensionais?: string[];
  };
  promessa?: string;
  urgencia?: string;
  url?: string;
  knowledgeBase?: ProductKnowledgeBase;
};

export type BriefingPersona = {
  id: string;
  nomeFicticio: string;
  perfilDemografico?: string;
  contextoAtual?: string;
  vocabulario?: string;
  canaisOrigem?: string[];
  dores?: string[];
  desejos?: string[];
  frustracoes?: string[];
  medos?: string[];
  objecoes?: string[];
  gatilhosEmocionais?: string[];
};

export type BriefingResultado = {
  id: string;
  tipo: "proprio" | "aluno";
  descricao: string;
  metrica?: string;
};

export type EscalaRadarScores = {
  E: number;
  S: number;
  C: number;
  A: number;
  L: number;
  A2: number;
};

export type CompanyBriefing = {
  id: string;
  companyId: string;
  projectId: string | null;
  nomeEmpresa: string | null;
  segmento: string | null;
  historia: string | null;
  posicionamento: string | null;
  diferencial: string | null;
  credenciais: string[];
  resultados: BriefingResultado[];
  publicoAlvo: string | null;
  tomVoz: string[];
  temasProibidos: string[];
  concorrentes: string[];
  produtos: BriefingProduct[];
  personas: BriefingPersona[];
  identidadeVisual: Record<string, unknown>;
  metricasAtuais: Record<string, unknown>;
  escalaRadar: EscalaRadarScores | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SaveBriefingInput = {
  nomeEmpresa?: string | null;
  segmento?: string | null;
  historia?: string | null;
  posicionamento?: string | null;
  diferencial?: string | null;
  credenciais?: string[];
  resultados?: BriefingResultado[];
  publicoAlvo?: string | null;
  tomVoz?: string[];
  temasProibidos?: string[];
  concorrentes?: string[];
  produtos?: BriefingProduct[];
  personas?: BriefingPersona[];
  identidadeVisual?: Record<string, unknown>;
  metricasAtuais?: Record<string, unknown>;
  escalaRadar?: EscalaRadarScores | null;
  markCompleted?: boolean;
};

export type BriefingSuggestions = {
  nomeEmpresa?: string;
  segmento?: string;
  historia?: string;
  posicionamento?: string;
  diferencial?: string;
  credenciais?: string[];
  publicoAlvo?: string;
  tomVoz?: string[];
  produtos?: Array<{
    nome: string;
    tipo?: string;
    preco?: string;
    descricao?: string;
    promessa?: string;
    mecanismoUnicoSolucao?: string;
    garantia?: string;
  }>;
  personas?: Array<{
    nomeFicticio: string;
    perfilDemografico?: string;
    dores?: string[];
    desejos?: string[];
    objecoes?: string[];
  }>;
  resultados?: Array<{
    tipo: "proprio" | "aluno";
    descricao: string;
    metrica?: string;
  }>;
  concorrentes?: string[];
};

export type VisualIdentity = {
  /** Cores hex da marca (top 6, ex: ["#FE7000", "#0D1526"]) */
  colors: string[];
  /** Fontes principais detectadas no CSS */
  fonts: string[];
  /** URL absoluta do logo (og:image / apple-touch-icon / favicon) */
  logoUrl: string | null;
  /** Theme color do meta tag (cor primária declarada) */
  themeColor: string | null;
  /** Title da página */
  title: string | null;
};

export type AnalyzeUrlResult = {
  url: string;
  scrapedAt: string;
  pageTitle?: string;
  suggestions: BriefingSuggestions;
  /** Identidade visual extraída do HTML/CSS (não usa Vision/screenshot). */
  visualIdentity: VisualIdentity;
  rawMarkdownChars: number;
  costUsd: number;
};

export type BriefingAttachment = {
  id: string;
  briefingId?: string;
  /** Asset bruto vinculado. Pra renderizar imagem: `/api/assets/{assetId}/content` */
  assetId: string;
  originalFilename: string | null;
  contentType: string;
  byteSize: number;
  description: string | null;
  /** "miv-logo" pra logos, "miv" pra demais materiais visuais, null pra
   *  anexos genéricos do briefing. */
  category: string | null;
  hasPreview: boolean;
  parsedAt: string | null;
  createdAt: string;
};

export type AnalyzeDocResult = {
  filename: string;
  contentType: string;
  byteSize: number;
  parsedChars: number;
  suggestions: BriefingSuggestions;
  costUsd: number;
};

export const briefingApi = {
  get: (companyId: string) =>
    api.get<CompanyBriefing | null>(`/companies/${companyId}/briefing`),
  save: (companyId: string, input: SaveBriefingInput) =>
    api.put<CompanyBriefing>(`/companies/${companyId}/briefing`, input),
  analyzeUrl: (companyId: string, url: string) =>
    api.post<AnalyzeUrlResult>(`/companies/${companyId}/briefing/analyze-url`, { url }),
  /**
   * Upload + análise por IA. O arquivo é processado em memória e descartado
   * pelo backend; só persistem os campos que o user aceitar aplicar.
   */
  analyzeDocument: (companyId: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.postForm<AnalyzeDocResult>(
      `/companies/${companyId}/briefing/analyze-document`,
      fd,
    );
  },

  // ─── Anexos do briefing (compartilhado com MIV via `category`) ─────────
  listAttachments: (companyId: string, category?: string) =>
    api.get<BriefingAttachment[]>(
      `/companies/${companyId}/briefing/attachments${category ? `?category=${encodeURIComponent(category)}` : ""}`,
    ),
  uploadAttachment: (
    companyId: string,
    file: File,
    opts: { description?: string; category?: string } = {},
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.description) fd.append("description", opts.description);
    if (opts.category) fd.append("category", opts.category);
    return api.postForm<BriefingAttachment>(
      `/companies/${companyId}/briefing/attachments`,
      fd,
    );
  },
  deleteAttachment: (companyId: string, attachmentId: string) =>
    api.delete<{ ok: true }>(
      `/companies/${companyId}/briefing/attachments/${attachmentId}`,
    ),
};
